import { isValid, parse } from 'date-fns';
import { formatDateTime } from '@/shared/lib';

/**
 * Метка места в переписке в сохранённом тексте комментария: `[дд.мм.гггг чч:мм]` — минута отправки
 * элемента переписки в UTC.
 *
 * Единственный источник этой записи: по нему метка и распознаётся при показе комментария
 * (маркер в `ui/commentMarkers.ts`), и переводится здесь во время смотрящего и обратно.
 * Хранится в UTC: метку ставит языковая модель, которой часовой пояс смотрящего неизвестен,
 * и запись не должна зависеть от того, кто её смотрит или правит.
 */
export const STORED_MESSAGE_MARK_PATTERN = /\[(\d{2}\.\d{2}\.\d{4} \d{2}:\d{2})\]/;

/** Та же метка для сплошного поиска по тексту — флаги регулярки у маркера свои */
const STORED_MESSAGE_MARK_SEARCH = new RegExp(STORED_MESSAGE_MARK_PATTERN.source, 'g');

/**
 * Запись момента в метке — та же, что над облаком переписки: ею метка и хранится (в UTC),
 * и набирается при правке (во времени смотрящего)
 */
const SENT_AT_FORMAT = 'dd.MM.yyyy HH:mm';

const padTwo = (value: number): string => String(value).padStart(2, '0');

/** Минута момента записью метки — в UTC */
const formatUtcMinute = (date: Date): string =>
  `${padTwo(date.getUTCDate())}.${padTwo(date.getUTCMonth() + 1)}.${date.getUTCFullYear()} ` +
  `${padTwo(date.getUTCHours())}:${padTwo(date.getUTCMinutes())}`;

/**
 * Минута отправки элемента переписки так, как её записывает метка места. Момент, который
 * датой не читается, — undefined.
 */
export const toMessageMarkSentAt = (createdAt: string): string | undefined => {
  const date = new Date(createdAt);
  return Number.isNaN(date.getTime()) ? undefined : formatUtcMinute(date);
};

/** Момент, записанный меткой; запись несуществующей даты (`31.02`) — undefined */
const parseStoredSentAt = (sentAt: string): Date | undefined => {
  // Пояс дописывается к записи: без него разбор прочёл бы её во времени смотрящего
  const date = parse(`${sentAt} Z`, `${SENT_AT_FORMAT} X`, new Date());
  return isValid(date) ? date : undefined;
};

/**
 * Подпись метки места во времени смотрящего — та же, которой облако переписки показывает время
 * отправки. Запись, которая датой не читается, остаётся как есть.
 */
export const formatMessageMarkLabel = (sentAt: string): string => {
  const date = parseStoredSentAt(sentAt);
  return date ? formatDateTime(date) : sentAt;
};

/**
 * Показывает сохранённые метки места комментария во времени смотрящего: поле правки показывает
 * их так же, как сам комментарий.
 */
export const formatCommentMessageMarks = (comment: string): string =>
  comment.replace(STORED_MESSAGE_MARK_SEARCH, (_mark, sentAt: string) => `[${formatMessageMarkLabel(sentAt)}]`);

/**
 * Приводит метки места, набранные во времени смотрящего, к записи хранения — в UTC.
 * Набранное, что датой не читается, остаётся текстом как есть.
 */
export const normalizeCommentMessageMarks = (comment: string): string =>
  comment.replace(STORED_MESSAGE_MARK_SEARCH, (mark, typed: string) => {
    const date = parse(typed, SENT_AT_FORMAT, new Date());
    return isValid(date) ? `[${formatUtcMinute(date)}]` : mark;
  });
